import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventService } from '@app/events/shared/event.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { DatePickerComponent } from '@app/partials/form/date-picker/date-picker.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { TimePickerComponent } from '@app/partials/form/time-picker/time-picker.component';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { ProductItemFormComponent } from '@app/events/view/product-item-form.component';
import { ProductItemEditFormComponent } from '@app/events/view/product-item-edit-form.component';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { Edit, LucideAngularModule, Trash } from 'lucide-angular';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { CurrencyPipe } from '@angular/common';
import { TextareaComponent } from '@app/partials/form/textarea/textarea.component';
import { FeatureFlags } from '@app/config/feature-flags';
import { RouterLink } from '@angular/router';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { EventProductItemService } from '@app/events/shared/event-product-item.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';

@Component({
  standalone: true,
  selector: 'app-event-timeline-item-edit',
  templateUrl: './timeline-item-edit.component.html',
  styleUrls: [],
  providers: [provideFlatpickrDefaults()],
  imports: [
    ButtonComponent,
    CheckboxComponent,
    DatePickerComponent,
    TextInputComponent,
    TimePickerComponent,
    ProductItemFormComponent,
    ProductItemEditFormComponent,
    LucideAngularModule,
    CurrencyPipe,
    TextareaComponent,
    RouterLink,
    SelectComponent,
  ],
})
export class TimelineItemEditComponent implements OnInit {
  @Input() public event!: EventModel;
  @Input() public item!: EventTimelineItemModel;
  @Output() toggleOpen = new EventEmitter<any>();
  @Output() itemUpdated = new EventEmitter<EventTimelineItemModel>();

  protected processing = false;
  protected productItems: EventProductItemModel[] = [];
  protected linkedProductItemIdsControl = new FormControl<number[]>([], { nonNullable: true });
  protected existingProductChoices: { value: number; label: string }[] = [];

  protected readonly EditIcon = Edit;
  protected readonly TrashIcon = Trash;
  protected readonly showProductManagement = FeatureFlags.ITINERARY_PRODUCT_MANAGEMENT;

  protected form: FormGroup<{
    date: FormControl<string | null>;
    time: FormControl<string | null>;
    is_published: FormControl<false | true | null>;
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;

  protected editProductItem?: EventProductItemModel;

  constructor(
    private eventService: EventService,
    private eventProductItemService: EventProductItemService,
    private confirmationModal: ConfirmationModalService,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
      is_published: new FormControl(false),
      date: new FormControl('', [Validators.required]),
      time: new FormControl('', [Validators.required]),
      description: new FormControl(''),
    });
  }

  ngOnInit(): void {
    this.form.patchValue({
      name: this.item.name,
      is_published: this.item.is_published,
      date: (this.item.datetime as unknown as string).split(' ')[0],
      time: (this.item.datetime as unknown as string).split(' ')[1],
      description: this.item.description,
    });
    this.productItems = [...this.item.event_product_items];
    this.linkedProductItemIdsControl.setValue(
      this.productItems
        .map((productItem) => productItem.id)
        .filter((id): id is number => id !== null && id !== undefined),
    );
    this.loadExistingProducts();
    this.trackLinkedProductSelectionChanges();
  }

  save() {
    if (!this.form) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    const data = this.form.value as any;
    data.event_id = this.event.id;

    // Clean product items: remove nested objects and negative IDs
    data.product_items = this.productItems.map((item) => {
      const cleanedItem: any = {
        quantity: item.quantity,
        amount: item.amount,
        date_purchased: item.date_purchased,
        is_paid: item.is_paid,
        product_id: item.product_id,
        event_id: item.event_id,
      };

      // Only include ID for existing products (positive IDs), omit for new products (negative IDs)
      if (item.id && item.id > 0) {
        cleanedItem.id = item.id;
      }

      // Clean variant selections: keep only essential fields, remove nested objects
      if (item.variant_selections && item.variant_selections.length > 0) {
        cleanedItem.variant_selections = item.variant_selections.map((selection: any) => ({
          variant_id: selection.variant_id,
          variant_option_id: selection.variant_option_id,
          quantity: selection.quantity,
          notes: selection.notes,
        }));
      }

      return cleanedItem;
    });
    data.linked_product_item_ids = this.linkedProductItemIdsControl.value ?? [];

    this.processing = true;

    this.eventService.updateEventTimelineItem(data, this.item.id).subscribe({
      next: (response) => {
        this.itemUpdated.emit(response.data);
        this.cancel();
        this.processing = false;
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
      },
    });
  }

  cancel() {
    this.form.reset();
    this.toggleOpen.emit();
  }

  productItemCreated($event: EventProductItemModel) {
    this.productItems.push($event);
    this.form.markAsDirty();
  }

  removeProductItem(id: any, productItem: EventProductItemModel) {
    // VG-342: Check feature flag before blocking removal
    if (this.isEventConfirmed) {
      return;
    }

    // Block Venue Hire deletion for wedding events
    if (this.isVenueHireProduct(productItem) && this.event.category === 'Wedding') {
      this.confirmationModal.alert({
        title: 'Venue Hire cannot be removed',
        message: 'Venue Hire cannot be removed from wedding events. This product is required for payment calculations.',
        confirmLabel: 'OK',
      });
      return;
    }

    this.productItems = this.productItems.filter((item) => item !== productItem);
    const currentLinkedIds = this.linkedProductItemIdsControl.value ?? [];
    if (id && id > 0) {
      this.linkedProductItemIdsControl.setValue(currentLinkedIds.filter((linkedId) => linkedId !== id));
    }
    this.form.markAsDirty();
  }

  editItem(productItem: EventProductItemModel) {
    // VG-342: Check feature flag before blocking edit
    if (this.isEventConfirmed) {
      return;
    }
    this.editProductItem = productItem;
  }

  closeEditForm() {
    this.editProductItem = undefined;
  }

  productItemUpdated(data: { data: any; id: number }) {
    // Ensure we're passing all necessary data including variant information
    const updatedData = {
      ...data.data,
      event_id: this.event.id,
    };

    this.eventService.updateEventProductItem(updatedData, data.id).subscribe({
      next: (response) => {
        const index = this.productItems.findIndex((item) => item.id === data.id);
        if (index !== -1) {
          this.productItems[index] = response.data;
        }
        // Do not reassign this.item.event_product_items here - it causes premature state overwrites
        // The timeline item's products will be updated when the timeline item is saved
        this.editProductItem = undefined;
        this.form.markAsDirty();
      },
      error: (res) => {
        console.error('Error updating product item:', res);
      },
    });
  }

  // VG-281: TEMPORARILY DISABLED - Paid status feature (2025-09-30)
  // To revert: Uncomment this method and the UI toggle in timeline-item-edit.component.html (lines 88-115)
  // toggleProductItemPaid(productItem: EventProductItemModel) {
  //   productItem.is_paid = !productItem.is_paid;
  //
  //   const data = {
  //     quantity: productItem.quantity,
  //     amount: productItem.amount,
  //     date_purchased: productItem.date_purchased,
  //     is_paid: productItem.is_paid,
  //     event_id: this.event.id,
  //     product_id: productItem.product_id,
  //   };
  //
  //   this.eventService.updateEventProductItem(data, productItem.id).subscribe({
  //     next: (response) => {
  //       const index = this.productItems.findIndex((item) => item.id === response.data.id);
  //       if (index !== -1) {
  //         this.productItems[index] = response.data;
  //       }
  //       this.item.event_product_items = this.productItems;
  //       this.itemUpdated.emit(this.item);
  //     },
  //     error: (res) => {
  //       console.error('Error updating product item:', res);
  //     },
  //   });
  // }

  // VG-342: Check both feature flag and event status
  get isEventConfirmed(): boolean {
    return FeatureFlags.PRICE_LOCKING && this.event?.status === 'Confirmed';
  }

  // Check if product is Venue Hire
  isVenueHireProduct(productItem: EventProductItemModel): boolean {
    return productItem.product.name.toLowerCase() === 'venue hire';
  }

  // Check if Venue Hire can be deleted (only for non-wedding events)
  canDeleteProduct(productItem: EventProductItemModel): boolean {
    if (this.isEventConfirmed) {
      return false;
    }
    return !(this.isVenueHireProduct(productItem) && this.event.category === 'Wedding');
  }

  getProductDisplayPrice(productItem: EventProductItemModel): number {
    if (!productItem) {
      return 0;
    }
    const calculated = productItem.calculated_price ?? null;
    if (calculated !== null && calculated !== undefined) {
      return calculated;
    }
    if (productItem.amount !== null && productItem.amount !== undefined) {
      return productItem.amount;
    }
    return 0;
  }

  private loadExistingProducts(): void {
    this.eventProductItemService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 500,
          filter: [{ field: 'event_id', value: this.event.id }],
        }),
      )
      .subscribe({
        next: (res) => {
          this.existingProductChoices = (res.data ?? []).map((item) => ({
            value: item.id,
            label: this.formatExistingProductLabel(item),
          }));
        },
      });
  }

  private trackLinkedProductSelectionChanges(): void {
    this.linkedProductItemIdsControl.valueChanges.subscribe(() => {
      this.form.markAsDirty();
    });
  }

  private formatExistingProductLabel(item: EventProductItemModel): string {
    const qty = item.quantity ?? 0;
    const linkedName = item.event_timeline_item?.name;
    const linkSuffix = linkedName ? ` (Linked: ${linkedName})` : ' (Unscheduled)';

    return `${item.product?.name || 'Product'} x${qty}${linkSuffix}`;
  }
}
